import {useSelector} from 'react-redux';
import {Box, Typography} from '@mui/material';
import Avatar from '@jobseeker/lotus/components/Avatar';
import {getOldLink} from '@jobseeker/lotus/utils';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {BaseSyntheticEvent, useEffect} from 'react';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {Image} from '@jobseeker/lotus/components/Image';
import {LockPrem, LockBackground} from '@jobseeker/lotus/assets/icons';

import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
import type {AppState} from '@jobseeker/common-core/types';
import {Loader} from '@jobseeker/lotus/components';

type JobStatusBannerProps = {
  setShowProMembershipPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  isStatusChangeCohortUser?: boolean;
};
const JobStatusBanner = ({
  setShowProMembershipPrompt,
  isStatusChangeCohortUser,
}: JobStatusBannerProps) => {
  const jobInfo: JB = useSelector((state: AppState) => state.jobDetail);
  const {isPro} = useSelector((state: AppState) => state.auth);
  const theme = useWrappedTheme();
  const isAboveMobile = useIsLargeDevice();
  const isMobileWeb = !isAboveMobile;
  const shouldShowMobileCohortBanner =
    isMobileWeb && !isPro && isStatusChangeCohortUser === true;
  const isCohortResolved = typeof isStatusChangeCohortUser === 'boolean';
  const canViewApplicationStatus = isPro || isStatusChangeCohortUser === true;
  const shouldShowUpgradeWall =
    !isPro && isCohortResolved && !canViewApplicationStatus;

  const handleChange = (e: BaseSyntheticEvent) => {
    e?.preventDefault();
  };

  useEffect(() => {
    if (!shouldShowUpgradeWall || isMobileWeb) {
      return;
    }
    handleTracking('jsSCBanner_View', {
      event_category: 'UpgradeToPro',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    });
  }, [isMobileWeb, shouldShowUpgradeWall]);

  useEffect(() => {
    if (
      !shouldShowMobileCohortBanner ||
      isStatusChangeCohortUser !== true ||
      !jobInfo?.id
    ) {
      return;
    }
    TrackEventUBA('appDownloadLinkageView', 'view', {
      pageName: 'JD Page',
      sessionUsername: sessionStorage.getItem('userId') || '',
      linkageName: 'Recruiter Action App Install Linkage',
      linkageType: 'Banner',
    });
  }, [shouldShowMobileCohortBanner, jobInfo?.id, isStatusChangeCohortUser]);

  if (isMobileWeb && !isPro && !isCohortResolved) return <Loader />;

  if (shouldShowMobileCohortBanner) {
    return (
      <Box
        borderRadius={'15px'}
        mb={2}
        flexShrink={0}
        display={'block'}
        alignItems={{md: 'center'}}
        sx={{
          backgroundColor: '#FAFFFD',
          border: '1px solid #C0DCCE',
          position: 'relative',
          overflow: 'visible',
          opacity: 1,
          boxShadow: '0 6px 0 0 #C0DCCE',
        }}
        onClick={() => {
          TrackEventUBA('appDownloadLinkageClick', 'click', {
            pageName: 'JD Page',
            jobID: jobInfo.id,
            sessionUsername: sessionStorage.getItem('userId') || '',
            linkageName: 'Recruiter Action App Install Linkage',
            linkageType: 'Banner',
          });
          openUrl(
            `https://iimjobs.onelink.me/TyLF/q6ti4zw4?deep_link_value=job_detail&deep_link_sub1=${jobInfo.id}`,
            '_blank'
          );
        }}
      >
        <Box sx={{position: 'relative', zIndex: 1}}>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: {xxs: 220, md: 280},
              textAlign: 'center',
              color: '#D9CEC0',
              opacity: 0.55,
              filter: 'blur(2px)',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            <Typography
              fontSize={{xxs: 8, md: 10}}
              fontWeight={600}
              lineHeight={{xxs: '12px', md: '14px'}}
            >
              Recruiter has taken action on your application
            </Typography>
            <Typography
              fontSize={{xxs: 8, md: 10}}
              fontWeight={500}
              lineHeight={{xxs: '12px', md: '14px'}}
            >
              Track recruiter activity in real time on the app
            </Typography>
          </Box>
          <Box
            sx={{
              mx: 'auto',
              mt: 1.5,
              width: 56,
              height: 56,
              position: 'relative',
            }}
          >
            <Image
              src={LockBackground}
              width={56}
              height={56}
              alt="Lock Background"
            />
            <Box
              sx={{
                position: 'absolute',
                top: 14,
                left: 18,
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image src={LockPrem} width={20} height={20} alt="lock" />
            </Box>
          </Box>

          <Box
            px={2}
            pb={1.5}
            width={'100%'}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            gap={0.5}
          >
            <Box
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              gap={0.5}
            >
              <Box display={'inline-block'} textAlign={'center'}>
                <Typography
                  fontSize={{xxs: 13, md: 12, lg: 14}}
                  fontWeight={600}
                  lineHeight={{xxs: '19px', lg: '20px'}}
                  textAlign={'center'}
                  color="#49966E"
                  display="inline-block"
                >
                  Your application status has been updated
                </Typography>
              </Box>

              <Box
                fontSize={11}
                lineHeight={'16px'}
                fontWeight={500}
                color={'#333333'}
                textAlign={'center'}
                px={0.5}
                mb={1}
              >
                Track recruiter activity in real time on the app
              </Box>
            </Box>

            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              width={'100%'}
            >
              <Button
                sx={{
                  background: '#49966E',
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '20px',
                  padding: '10px 0',
                  ml: 0,
                  whiteSpace: 'nowrap',
                  borderRadius: '50px',
                  width: '100%',
                  minHeight: 40,
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#49966E',
                    boxShadow: 'none',
                  },
                }}
                variant="contained"
                size="small"
              >
                Install App to Unlock
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      borderRadius={4}
      mb={0.5}
      minHeight={{xxs: 91, md: 66}}
      flexShrink={0}
      display={{xxs: 'block', md: 'flex'}}
      alignItems={{md: 'center'}}
      sx={{
        background:
          'linear-gradient(243deg, rgba(215, 220, 210, 0.96) 1.33%, rgba(215, 220, 210, 0.96) 62.26%, rgba(233, 222, 197, 0.96) 87.65%, rgba(215, 195, 226, 0.96) 100.76%, rgba(255, 255, 255, 0.00) 100.77%)',
      }}
    >
      <Box
        px={{xxs: 1, lg: 2}}
        py={1.5}
        width={'100%'}
        display={{xxs: 'block', md: 'flex'}}
        justifyContent={'space-between'}
      >
        <Box display={'flex'} alignItems={{md: 'center'}}>
          <Avatar
            sx={{width: 40, height: 40}}
            imgUrl={
              !jobInfo?.confidential
                ? jobInfo?.recruiter?.profilePicUrl
                : `${getOldLink('/resources/img/user.jpg')}`
            }
          />
          <Box
            display={'inline-block'}
            pl={{xxs: 0.5, md: 0.5, lg: 1.5}}
            alignSelf={isPro ? 'center' : 'auto'}
          >
            <Typography
              fontSize={{xxs: 10, md: 12, lg: 14}}
              fontWeight={600}
              lineHeight={{xxs: '16px', lg: '20px'}}
              mb={{xxs: 0, md: 0.25, lg: 0.5}}
            >
              {`${
                !jobInfo?.confidential
                  ? jobInfo?.recruiter?.recruiterName
                  : 'Verified Recruiter'
              } has updated your job application status.`}
            </Typography>
            {shouldShowUpgradeWall && (
              <Typography
                fontSize={{xxs: 10, lg: 14}}
                fontWeight={500}
                lineHeight={{xxs: '16px', lg: '20px'}}
              >
                See the recruiter’s action by upgrading to
                {!isAboveMobile ? <br /> : ' '}
                <b>PRO-Membership!</b>
              </Typography>
            )}
          </Box>
        </Box>
        <Box display={'flex'} ml={{xxs: 6, md: 0}} alignItems={'center'}>
          {shouldShowUpgradeWall && (
            <Typography
              fontSize={{xxs: 16, md: 18}}
              fontWeight={600}
              lineHeight={'24px'}
              color={'primary'}
              sx={{
                filter: 'blur(5px)',
              }}
              onCut={handleChange}
              onCopy={handleChange}
            >
              STATUS CHANGED
            </Typography>
          )}
          {jobInfo?.recruiterActionText && canViewApplicationStatus && (
            <Typography
              borderRadius={0.5}
              variant="subtitle2"
              sx={{
                px: 1.5,
                py: 0.35,
                backgroundColor: 'rgba(145, 184, 121, 0.16)',
                justifyContent: 'center',
                alignSelf: 'center',
                color: 'primary.main',
              }}
            >
              {jobInfo?.recruiterActionText}
            </Typography>
          )}
          {shouldShowUpgradeWall && (
            <Button
              sx={{
                background: theme.palette.pro.main,
                py: 1,
                px: 2,
                fontSize: 12,
                my: {xxs: 0, md: 0.52},
                ml: {xxs: 2, md: 1, lg: 3.75},
                width: '135px',
              }}
              variant="contained"
              size="small"
              onClick={() => {
                setShowProMembershipPrompt(true);
                handleTracking('jsSCBanner_Click', {
                  event_category: 'UpgradeToPro',
                  UserId: sessionStorage.getItem('userId') || 'NA',
                  Device: sessionStorage.getItem('device') || 'desktop',
                  Device_Type: sessionStorage.getItem('deviceType') || 'web',
                  Status: sessionStorage.getItem('status') || 'LoggedOut',
                });
              }}
            >
              Upgrade to PRO
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default JobStatusBanner;
